import React from "react";
import Outcome from "../../../../../mxGraph/HCLTreeNode/HCLTreeVertexValue/Outcome";
import ReferenceTypes from "../../../../../mxGraph/HCLTreeNode/HCLTreeVertexValue/ReferenceTypes";
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import {TreeTypes} from "../Tools/TreeDictionaries";
import TreePickerDialog from "../Dialogs/TreePickerDialog";
import HCLTreeVertex from "../../../../../mxGraph/HCLTreeNode/HCLTreeVertex";
import FaultTreeTransferGateVertexValue
  from "../../../../../mxGraph/HCLTreeNode/FaultTreeNode/FaultTreeTransferGateNode/FaultTreeTransferGateVertexValue";
import {FaultTreeMxGraphViewJSON} from "../MxGraphViews/FaultTreeMxGraphEditorView";
import HCLTreeMxGraphView from "../MxGraphViews/HCLTreeMxGraphView";
import HclApiManager from "../../../../../Api/HclApiManager";

// @ts-ignore
import CircularIndeterminate from 'hcla-web-frontend-primitives/app/components/primitives/Progress/CircularIndeterminate';
import {Theme, Typography, WithStyles} from "@material-ui/core";
import createStyles from "@material-ui/core/styles/createStyles";
import withStyles from "@material-ui/core/styles/withStyles";
import {BayesianNetworkMxGraphViewJSON} from "../MxGraphViews/BayesianNetworkMxGraphEditorView";
import {TreeContext} from "../../TreeContext";

const styles = (theme: Theme) => createStyles({
  graphContainer: {
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    height: "40vh",
    overflow: 'auto',
    border: '1px solid #999',
    borderRadius: "12px",
    position: 'relative',
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  },

  selectButton: {
    top: "50%",
    transform: "translateY(-50%)",
    height: "60%"
  }
});

interface Props extends WithStyles<typeof styles> {
  outcome: Outcome;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement> | React.SyntheticEvent<{}, Event>, name: string, multiple?: boolean) => void;
  name: string;
  disabled?: boolean;
  treeTypesToShow?: any;
}

interface State {
  isDialogOpen: boolean;
  data: FaultTreeMxGraphViewJSON | BayesianNetworkMxGraphViewJSON;
  loading: boolean;
  dataValid: boolean;
}

class OutcomeInput extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);
    this.state = {
      isDialogOpen: false,
      data: null,
      loading: true,
      dataValid: false
    };

  }

  private handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { onChange, name } = this.props;
    onChange(e, name);
  };

  private toggleDialogOpen = () => {
    this.setState(prevState => ({ isDialogOpen: !prevState.isDialogOpen }));
  };

  private handleSet = (e: React.SyntheticEvent<{}, Event>, selectedVertex: HCLTreeVertex) => {
    const { onChange, name } = this.props;
    // @ts-ignore
    e.target.value = selectedVertex.toReferenceJSON();
    e.persist();
    onChange(e, name, true);
  };

  private setData = () => {
    const { outcome } = this.props;
    let status: number;
    if (outcome.getTreeId() !== 0) {
      this.setState({ loading: true }, () => {
        HclApiManager.getHclTreeWithMetadata(outcome.getTreeId())
          .then((response: any) => {
            status = response.status;
            return response.status && response.json();
          })
          .then((data: FaultTreeMxGraphViewJSON) => {
            this.setState({ data, loading: false, dataValid: status === 200 });
          });
      });
    } else {
      this.setState({loading: false});
    }
  };

  componentDidMount(): void {
    this.setData();
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
    if (prevProps.outcome.getTreeId() !== this.props.outcome.getTreeId() || prevProps.outcome.getName() !== this.props.outcome.getName()) {
      this.setData();
    }
  }

  render() {
    const { classes, outcome, disabled, treeTypesToShow } = this.props;
    const { isDialogOpen, loading, dataValid, data } = this.state;

    const referenceTypes = [ReferenceTypes.GATES, ReferenceTypes.BASIC_EVENTS, ReferenceTypes.HOUSE_EVENTS];

    return (
      <React.Fragment>
        <TreeContext.Consumer>
          {
            value => {
              const { basePath, isHCLFlatList } = value;

              const modelTypeRoutes = {
                "PR": "/project",
                "SU": "/subsystem",
                "CO": "/component",
              };

              const ftBasePath = data && data.tree_type === "f" ? modelTypeRoutes[data.model.model_tag] : null;

              return (
                <React.Fragment>
                  <Grid item xs={12}>
                    <div className={classes.graphContainer}>
                      {
                        (loading && <CircularIndeterminate />)
                        ||
                        (dataValid && <HCLTreeMxGraphView data={data} targetCellKey={outcome.getName()} basePath={ftBasePath && !isHCLFlatList ? ftBasePath : basePath} />)
                        ||
                        <Typography variant="h5" color="textSecondary">Reference Tree Does not Exist</Typography>
                      }
                    </div>
                  </Grid>
                  <Grid container spacing={8} alignItems="center" justify="space-evenly" direction="row">
                    <Grid item xs={12} sm={2}>
                      <Button
                        fullWidth
                        variant="outlined"
                        size="large"
                        onClick={this.toggleDialogOpen}
                        color="secondary"
                      >
                        Select
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <TextField
                        label="Linked Fault Tree ID"
                        name="tree_id"
                        variant="outlined"
                        value={outcome.getTreeId()}
                        onChange={this.handleChange}
                        type="number"
                        margin="normal"
                        fullWidth
                        disabled={disabled}
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <TextField
                        label="Linked Node ID"
                        name="name"
                        variant="outlined"
                        value={outcome.getName()}
                        onChange={this.handleChange}
                        margin="normal"
                        fullWidth
                        disabled={disabled}
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <TextField
                        label="Link Type"
                        variant="outlined"
                        name="reference_type"
                        value={`${outcome.getReferenceType()}`}
                        onChange={this.handleChange}
                        margin="normal"
                        fullWidth
                        select
                        SelectProps={{
                          MenuProps: {
                            disablePortal: true,
                          }
                        }}
                        disabled={disabled}
                      >
                        {
                          referenceTypes.map(referenceType =>
                            <MenuItem key={referenceType} value={referenceType}>{referenceType}</MenuItem>
                          )
                        }
                      </TextField>
                    </Grid>
                  </Grid>
                  <TreePickerDialog
                    open={isDialogOpen}
                    onClose={this.toggleDialogOpen}
                    onSet={this.handleSet}
                    treeTypes={treeTypesToShow}
                  />
                </React.Fragment>
              )
            }
          }
        </TreeContext.Consumer>
      </React.Fragment>
    )
  }
}

export default withStyles(styles)(OutcomeInput);
